import type {
  ApolloQueryResult,
  FetchResult,
  MutationOptions,
  OperationVariables,
  QueryOptions,
} from '@apollo/client/core';

export interface ApolloRuntimeClient {
  query<TData, TVariables extends OperationVariables = OperationVariables>(
    options: QueryOptions<TVariables, TData>,
  ): Promise<ApolloQueryResult<TData>>;
  mutate<TData, TVariables extends OperationVariables = OperationVariables>(
    options: MutationOptions<TData, TVariables>,
  ): Promise<FetchResult<TData>>;
}

export interface ActiveQueriesApolloRuntimeClient extends ApolloRuntimeClient {
  getObservableQueries(include: 'active'): Map<string, unknown>;
  refetchQueries(options: { include: 'active' }): Promise<readonly ApolloQueryResult<unknown>[]>;
}

export interface ActiveApolloQueriesRefetchBatch {
  queryCount: number;
  results: readonly ApolloQueryResult<unknown>[];
}

let runtimeClient: ApolloRuntimeClient | null = null;

export const setApolloRuntimeClient = (client: ApolloRuntimeClient): void => {
  runtimeClient = client;
};

export const getApolloRuntimeClient = (): ApolloRuntimeClient => {
  if (!runtimeClient) {
    throw new Error('Apollo runtime client is not initialized');
  }

  return runtimeClient;
};

export const getActiveQueriesApolloRuntimeClient = (): ActiveQueriesApolloRuntimeClient => {
  const client = getApolloRuntimeClient();

  if (!('getObservableQueries' in client) || !('refetchQueries' in client)) {
    throw new Error('Apollo runtime client does not support active query refetch');
  }

  return client as ActiveQueriesApolloRuntimeClient;
};

export const refetchActiveApolloQueries = async (
  client: ActiveQueriesApolloRuntimeClient = getActiveQueriesApolloRuntimeClient(),
): Promise<ActiveApolloQueriesRefetchBatch> => {
  const queryCount = client.getObservableQueries('active').size;
  const results = await client.refetchQueries({ include: 'active' });

  return {
    queryCount,
    results,
  };
};
